import { useState } from 'react';
import { styled } from '@mui/material/styles';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import RelatedCafe from './RelatedCafe';
import NoteComments from '../NoteComments';
import { toMonthShortName } from '../../../../utils/date';
import DOMPurify from 'dompurify';
import ChatIcon from '@mui/icons-material/Chat';
import SocialBtn from './SocialBtn';
import useMediaQuery from '@mui/material/useMediaQuery';

const AccordionContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('750')]: {
    paddingLeft: '10px',
  },
}));

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))({
  backgroundColor: 'transparent',
  borderTop: `2px solid #AA9178`,

  margin: '1px 0',
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
  borderBottom: `none`,
});

const AccordionSummary = styled(MuiAccordionSummary)({
  fontFamily: 'Fjalla one',
  fontWeight: '400',
  fontSize: '18px',
  lineHeight: '23px',
  padding: '0 11px',
  margin: '0',
  color: '#99989A',
  minHeight: 'auto',
  '&:hover .MuiAccordionSummary-content, &.Mui-expanded .MuiAccordionSummary-content': {
    color: '#AB9178',
  },
  '&.Mui-expanded .MuiAccordionSummary-content': {
    margin: '12px 0 10px',
  },
});

const AccordionDetailContainer = styled('div')({
  overflow: 'hidden',
});

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  height: '235px',
  overflowY: 'auto',

  '& p': {
    fontFamily: 'Raleway',
    fontWeight: '400',
    fontSize: '16px',
    lineHeight: '19px',
    color: '#000000',
  },

  [theme.breakpoints.down('700')]: {
    height: '100%',
  },
}));

const NoteDate = styled('div')({
  marginBottom: '7px',

  '& p': {
    fontFamily: 'Raleway',
    fontWeight: '400',
    fontSize: '16px',
    lineHeight: '19px',
    color: '#AB9178',
    margin: '0',
  },
});

const SocialBttn = styled('div')(({ theme }) => ({
  marginLeft: '8px',
  [theme.breakpoints.up('750')]: {
    display: 'none',
  },
}));

const hr = styled('span')(({ theme }) => ({
  width: '200px',
  borderBottom: '2px solid rgb(166,146,123)',
  [theme.breakpoints.down('750')]: {
    display: 'none',
  },
}));

const NoteTitleBox = styled('div')({
  '&>p': {
    margin: '0',
    fontFamily: 'Fjalla One',
    fontWeight: '400',
    fontSize: '17px',
    lineHeight: '21px',
    color: 'rgba(171, 145, 120, 1)',
  },
});

const NoteContentBox = styled('div')({
  paddingTop: '5px',
});

const NoteRelatedCafeBox = styled('div')({
  paddingTop: '15px',
  '&>p': {
    margin: '0',
    fontFamily: 'Fjalla One',
    fontWeight: '400',
    fontSize: '17px',
    lineHeight: '21px',
    color: 'rgba(171, 145, 120, 1)',
  },
});

const NoteCommentsBox = styled('div')({
  paddingTop: '15px',
});

const NoteCommentsAndBtnContainer = styled('div')({
  display: 'flex',
  '&>p': {
    margin: '0 13px 0 0',
    fontFamily: 'Fjalla One',
    fontWeight: '400',
    fontSize: '17px',
    lineHeight: '21px',
    color: 'rgba(171, 145, 120, 1)',
  },
});

const NoteInfoAccordion = ({ noteData, replyComment, handleClick }) => {
  const [expanded, setExpanded] = useState('panel1');
  const [shouldResponse, setShouldResponse] = useState(window.innerWidth < 700);
  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : panel);
  };
  let year = '';
  let month = '';
  let day = '';
  let date = '';
  let isEmpty = false;

  if (noteData.date !== undefined) {
    year = noteData.date.split('-')[0];
    month = toMonthShortName(parseInt(noteData.date.split('-')[1]));
    day = noteData.date.split('-')[2].split('T')[0];
    date = month + ' ' + day + ', ' + year;
  } else {
    isEmpty = true;
  }

  let cleanNoteContent = DOMPurify.sanitize(noteData.content, { USE_PROFILES: { html: true } });
  window.addEventListener('resize', () => {
    setShouldResponse(window.innerWidth < 700);
  });

  const mobileMatch = useMediaQuery((theme) => theme.breakpoints.down('750'));

  return (
    <>
      {mobileMatch ? (
        <>
          <AccordionContainer>
            <NoteTitleBox>
              <p>{noteData.title}</p>
            </NoteTitleBox>
            <NoteContentBox>
              <NoteDate>
                <p>{isEmpty ? ' ' : date}</p>
              </NoteDate>
              <Typography dangerouslySetInnerHTML={{ __html: cleanNoteContent }}></Typography>
            </NoteContentBox>
            <NoteRelatedCafeBox>
              <p>Related Cafe and Food</p>
              {noteData.relatedCafes.map((ele, id) => (
                <RelatedCafe cafe={ele} key={id} />
              ))}
            </NoteRelatedCafeBox>
            <NoteCommentsBox>
              <NoteCommentsAndBtnContainer>
                <p>Comments</p>
                <SocialBtn>
                  <ChatIcon onClick={(event) => handleClick()} />
                </SocialBtn>
              </NoteCommentsAndBtnContainer>
              <NoteComments noteData={noteData} replyComment={replyComment} />
            </NoteCommentsBox>
          </AccordionContainer>
        </>
      ) : (
        <>
          <AccordionContainer>
            <Accordion
              expanded={expanded === 'panel1' || shouldResponse}
              onChange={handleChange('panel1')}
            >
              <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
                {noteData.title}
              </AccordionSummary>
              <AccordionDetailContainer>
                <AccordionDetails>
                  <NoteDate>
                    <p>{isEmpty ? ' ' : date}</p>
                  </NoteDate>
                  <Typography dangerouslySetInnerHTML={{ __html: cleanNoteContent }}></Typography>
                </AccordionDetails>
              </AccordionDetailContainer>
            </Accordion>

            <Accordion
              expanded={expanded === 'panel2' || shouldResponse}
              onChange={handleChange('panel2')}
            >
              <AccordionSummary aria-controls="panel2d-content" id="panel2d-header">
                Related Cafe and Food
              </AccordionSummary>

              <AccordionDetailContainer>
                <AccordionDetails>
                  {noteData.relatedCafes.map((ele, id) => (
                    <RelatedCafe cafe={ele} key={id} />
                  ))}
                </AccordionDetails>
              </AccordionDetailContainer>
            </Accordion>

            <Accordion
              expanded={expanded === 'panel3' || shouldResponse}
              onChange={handleChange('panel3')}
            >
              <AccordionSummary aria-controls="panel3d-content" id="panel3d-header">
                Comments
                <SocialBttn>
                  <ChatIcon onClick={(event) => handleClick()} />
                </SocialBttn>
              </AccordionSummary>
              <AccordionDetailContainer>
                <AccordionDetails>
                  <NoteComments noteData={noteData} replyComment={replyComment} />
                </AccordionDetails>
              </AccordionDetailContainer>
            </Accordion>
          </AccordionContainer>
        </>
      )}
    </>
  );
};

export default NoteInfoAccordion;
